import React from 'react';
import { AlertCircle, CheckCircle2, XCircle, Lightbulb } from 'lucide-react';

const ContextDensityChecker = ({ contextDensity, isLocked }) => {
  if (isLocked || !contextDensity) {
    return null;
  }

  const { critical_skills_analysis = [], summary = '' } = contextDensity || {};

  if (!critical_skills_analysis || critical_skills_analysis.length === 0) {
    return null;
  }

  // Categorize skills according to triage rules
  const missingSkills = critical_skills_analysis.filter(
    skill => !skill.found_in_skills_list && !skill.found_in_projects && !skill.found_in_experience
  );
  const hollowSkills = critical_skills_analysis.filter(
    skill => skill.is_hollow === true && skill.found_in_skills_list
  );
  const optimizedSkills = critical_skills_analysis.filter(
    skill => !skill.is_hollow && (skill.found_in_projects || skill.found_in_experience)
  );

  return (
    <section className="bg-card border border-border rounded-lg p-6 space-y-4">
      <div className="flex items-center space-x-2 mb-4">
        <Lightbulb className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-semibold text-foreground">
          Recruiter's Eye View
        </h2>
        <span className="text-xs text-muted-foreground italic">
          (Context & Density Checker)
        </span>
      </div>

      {summary && (
        <div className="bg-muted/30 rounded-lg p-4 mb-4">
          <p className="text-sm text-foreground">{summary}</p>
        </div>
      )}

      {/* Missing Skills (Rule A) */}
      {missingSkills.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-error" />
            <h3 className="text-lg font-semibold text-foreground">
              Missing Skills ({missingSkills.length})
            </h3>
          </div>

          {missingSkills.map((skill, index) => (
            <div
              key={`missing-${index}`}
              className="bg-error/10 border border-error/20 rounded-lg p-4 space-y-3"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="font-semibold text-foreground">{skill.skill}</span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-error/20 text-error">
                      Missing
                    </span>
                  </div>

                  {skill.observation && (
                    <div className="mb-2">
                      <p className="text-sm font-medium text-foreground mb-1">Observation:</p>
                      <p className="text-sm text-muted-foreground">{skill.observation}</p>
                    </div>
                  )}

                  {skill.recruiter_perception && (
                    <div className="mb-2">
                      <p className="text-sm font-medium text-foreground mb-1">Recruiter Perception:</p>
                      <p className="text-sm text-muted-foreground italic">
                        "{skill.recruiter_perception}"
                      </p>
                    </div>
                  )}

                  {skill.action && (
                    <div className="mb-2">
                      <p className="text-sm font-medium text-foreground mb-1">Action:</p>
                      <p className="text-sm text-foreground">{skill.action}</p>
                    </div>
                  )}

                  {skill.project_suggestions && skill.project_suggestions.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-medium text-foreground">Project-Specific Suggestions:</p>
                      {skill.project_suggestions.map((proj, projIndex) => (
                        <div
                          key={`proj-${projIndex}`}
                          className="bg-muted/50 rounded-md p-3 text-sm"
                        >
                          <p className="font-medium text-foreground mb-1">
                            {proj.project_name || `Project ${projIndex + 1}`}
                          </p>
                          <p className="text-muted-foreground">{proj.suggested_addition}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hollow Skills (Rule B) */}
      {hollowSkills.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <XCircle className="w-5 h-5 text-warning" />
            <h3 className="text-lg font-semibold text-foreground">
              Hollow Skills ({hollowSkills.length})
            </h3>
          </div>

          {hollowSkills.map((skill, index) => (
            <div
              key={`hollow-${index}`}
              className="bg-warning/10 border border-warning/20 rounded-lg p-4 space-y-3"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="font-semibold text-foreground">{skill.skill}</span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-warning/20 text-warning">
                      Hollow Skill
                    </span>
                  </div>

                  {skill.observation && (
                    <div className="mb-2">
                      <p className="text-sm font-medium text-foreground mb-1">Observation:</p>
                      <p className="text-sm text-muted-foreground">{skill.observation}</p>
                    </div>
                  )}

                  {skill.recruiter_perception && (
                    <div className="mb-2">
                      <p className="text-sm font-medium text-foreground mb-1">Recruiter Perception:</p>
                      <p className="text-sm text-muted-foreground italic">
                        "{skill.recruiter_perception}"
                      </p>
                    </div>
                  )}

                  {skill.action && (
                    <div className="mb-2">
                      <p className="text-sm font-medium text-foreground mb-1">Action:</p>
                      <p className="text-sm text-foreground">{skill.action}</p>
                    </div>
                  )}

                  {skill.project_suggestions && skill.project_suggestions.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-medium text-foreground">Project-Specific Suggestions:</p>
                      {skill.project_suggestions.map((proj, projIndex) => (
                        <div
                          key={`proj-${projIndex}`}
                          className="bg-muted/50 rounded-md p-3 text-sm"
                        >
                          <p className="font-medium text-foreground mb-1">
                            {proj.project_name || `Project ${projIndex + 1}`}
                          </p>
                          <p className="text-muted-foreground">{proj.suggested_addition}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Optimized Skills (Rule C) */}
      {optimizedSkills.length > 0 && (
        <div className="space-y-4 mt-6">
          <div className="flex items-center space-x-2">
            <CheckCircle2 className="w-5 h-5 text-success" />
            <h3 className="text-lg font-semibold text-foreground">
              Well-Contextualized Skills ({optimizedSkills.length})
            </h3>
          </div>

          {optimizedSkills.map((skill, index) => (
            <div
              key={`optimized-${index}`}
              className="bg-success/10 border border-success/20 rounded-lg p-4"
            >
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-success" />
                <span className="font-medium text-foreground">{skill.skill}</span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-success/20 text-success">
                  Properly Contextualized
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Found in {skill.found_in_projects ? 'Projects' : ''}
                {skill.found_in_projects && skill.found_in_experience ? ' and ' : ''}
                {skill.found_in_experience ? 'Work Experience' : ''}
              </p>
            </div>
          ))}
        </div>
      )}

      {missingSkills.length === 0 && hollowSkills.length === 0 && optimizedSkills.length > 0 && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4 mt-4">
          <div className="flex items-center space-x-2">
            <CheckCircle2 className="w-5 h-5 text-success" />
            <p className="text-sm font-medium text-foreground">
              Great! All critical skills are properly contextualized in your experience sections.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default ContextDensityChecker;